/**
 * GamepadConnectionToasts — says a controller arrived or left, on ordinary screens.
 *
 * Renders nothing. It exists to turn a store event into a toast, and it must be mounted INSIDE
 * `ToastQueueProvider`: outside it `useToast()` silently returns a no-op API and the feature dies
 * with no error at all.
 *
 * STANDS DOWN while a local notice owner is held. The teleop overlay claims ownership because an
 * app toast is provably invisible there — the stack is z-50 under AppLayout, the overlay is
 * `fixed inset-0 z-[90]` with an opaque background inside the real Fullscreen API. Without this
 * check an operator in teleop would get the spoken and local notice AND an invisible toast queued
 * behind them.
 */

import { useToast } from '@/hooks/useToastQueue'
import { padLabel } from '@/lib/controller/gamepad-connection'
import { hasGamepadNoticeOwner } from '@/lib/controller/gamepad-connection-store'
import { useGamepadConnectionEvent } from '@/lib/controller/useGamepadConnectionEvent'
import { useEffect } from 'react'

export function GamepadConnectionToasts() {
  const event = useGamepadConnectionEvent()
  const { toast } = useToast()

  useEffect(() => {
    if (!event) return
    if (hasGamepadNoticeOwner()) return

    const name = padLabel(event.id)

    if (event.kind === 'connected') {
      toast({
        intent: 'success',
        title: 'Controller connected',
        ...(name ? { description: name } : {}),
      })

      return
    }

    // On a disconnect the useful second line is the CONSEQUENCE, not the name. This surface has no
    // telemetry and cannot know whether the robot was being driven, so it does not claim to —
    // the escalation to an error lives on the teleop notice, which does know.
    toast({
      intent: 'warning',
      title: 'Controller disconnected',
      description: 'Gamepad control is unavailable until it reconnects.',
    })
  }, [event, toast])

  return null
}
